// @ts-nocheck
/**
 * Divisions — the two-pane home a tournament director spends their day in.
 *
 * Left: every division in the event with live counts. Right: the screen for the
 * division that is selected. Both live in the URL
 * (/admin-dashboard/events/:eventId/divisions/:division/:screen) so any screen
 * can be bookmarked or sent to someone mid-event, and the selection survives a
 * reload. On a phone the left pane collapses to a picker above the screen.
 */
import { useEffect, useMemo } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Upload, Table2, AlertTriangle } from "lucide-react";
import { useDivisionNav } from "@/hooks/useDivisionNav";
import { useIsMobile } from "@/hooks/use-mobile";
import RenameDivisionButton from "@/components/admin/schedule/RenameDivisionButton";
import { DivisionNavContext, type DivisionNavContextValue } from "./context";

export const DIVISION_SCREENS = [
  { key: "details", label: "Division Details", count: null },
  { key: "teams", label: "Teams", count: (r: any) => r.teams },
  { key: "pools", label: "Pools", count: (r: any) => r.pools },
  { key: "pool-games", label: "Pool Games", count: (r: any) => r.pool_games },
  { key: "bracket-games", label: "Bracket Games", count: (r: any) => r.bracket_games },
  { key: "bracket", label: "Bracket", count: null },
  { key: "restrictions", label: "Schedule Restrictions", count: (r: any) => r.restrictions },
  { key: "places", label: "Set Division Places", count: (r: any) => r.places },
] as const;

const STORE = (eventId: string) => `rle:lastDivision:${eventId}`;

export default function EventDivisionsLayout() {
  const { eventId, division: rawDivision, "*": rest } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { rows, event, versionId, loading, error, refresh } = useDivisionNav(eventId);

  const division = rawDivision ? decodeURIComponent(rawDivision) : null;
  const screen = useMemo(() => {
    const seg = window.location.pathname.split("/").filter(Boolean).pop();
    return DIVISION_SCREENS.some((s) => s.key === seg) ? seg : "details";
  }, [rawDivision, rest, window.location.pathname]);

  // Remember the division per event, so returning lands where he left off.
  useEffect(() => {
    if (eventId && division) localStorage.setItem(STORE(eventId), division);
  }, [eventId, division]);

  // No division in the URL: use the remembered one, else the first.
  useEffect(() => {
    if (loading || division || rows.length === 0 || !eventId) return;
    const remembered = localStorage.getItem(STORE(eventId));
    const pick = rows.find((r) => r.division === remembered)?.division ?? rows[0].division;
    navigate(`/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(pick)}/details`, { replace: true });
  }, [loading, division, rows, eventId, navigate]);

  const current = rows.find((r) => r.division === division) ?? null;
  const base = `/admin-dashboard/events/${eventId}/divisions`;
  const go = (d: string) => navigate(`${base}/${encodeURIComponent(d)}/${screen}`);

  const ctx: DivisionNavContextValue = {
    eventId: eventId!,
    division: division ?? "",
    versionId,
    counts: current,
    event,
    refreshCounts: refresh,
  };

  const divisionList = (
    <nav className="space-y-1">
      {rows.map((r) => (
        <button
          key={r.division}
          onClick={() => go(r.division)}
          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
            r.division === division ? "bg-primary text-primary-foreground" : "hover:bg-muted"
          }`}
        >
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
            style={r.color ? { background: r.color } : undefined}
          />
          <span className="flex-1 truncate font-medium">{r.division}</span>
          <span className="text-xs opacity-75">{r.teams}</span>
        </button>
      ))}
    </nav>
  );

  const screenList = (
    <nav className="space-y-0.5">
      {DIVISION_SCREENS.map((s) => {
        const n = s.count && current ? s.count(current) : null;
        return (
          <NavLink
            key={s.key}
            to={`${base}/${encodeURIComponent(division ?? "")}/${s.key}`}
            className={({ isActive }) =>
              `block rounded-md px-3 py-1.5 text-sm ${isActive ? "bg-muted font-semibold" : "hover:bg-muted/60"}`
            }
          >
            {s.label}
            {n !== null && <span className="ml-1 text-muted-foreground">({n})</span>}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/admin-dashboard/events/${eventId}`}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Event
          </Link>
        </Button>
        <h1 className="text-xl font-semibold">Divisions</h1>
        {event?.name && <span className="text-sm text-muted-foreground">{event.name}</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link to={`/admin-dashboard/events/${eventId}/edit?step=divisions`}>
              <Plus className="mr-1 h-4 w-4" /> Add division
            </Link>
          </Button>
          {division && (
            <RenameDivisionButton
              eventId={eventId!}
              division={division}
              onDone={(newName) => {
                void refresh();
                navigate(`${base}/${encodeURIComponent(newName)}/${screen}`, { replace: true });
              }}
            />
          )}
          <Button size="sm" variant="outline" asChild>
            <Link to={`/admin-dashboard/events/${eventId}/registrations`}>
              <Upload className="mr-1 h-4 w-4" /> Import
            </Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to={`${base}/${encodeURIComponent(division ?? "")}/teams`}>
              <Table2 className="mr-1 h-4 w-4" /> Bulk add/edit
            </Link>
          </Button>
        </div>
      </div>

      {error && (
        <Card>
          <CardContent className="flex items-center gap-2 py-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" /> {error}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            This event has no divisions yet. Add one in the event details.
          </CardContent>
        </Card>
      ) : isMobile ? (
        <div className="space-y-3">
          <Select value={division ?? undefined} onValueChange={go}>
            <SelectTrigger><SelectValue placeholder="Choose a division" /></SelectTrigger>
            <SelectContent>
              {rows.map((r) => (
                <SelectItem key={r.division} value={r.division}>
                  {r.division} · {r.teams} team{r.teams === 1 ? "" : "s"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={screen} onValueChange={(s) => navigate(`${base}/${encodeURIComponent(division ?? "")}/${s}`)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {DIVISION_SCREENS.map((s) => {
                const n = s.count && current ? s.count(current) : null;
                return (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}{n !== null ? ` (${n})` : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <DivisionNavContext.Provider value={ctx}>
            {/* Wide grids stay usable on a phone by scrolling sideways, not by
                pushing the two pickers off the screen. */}
            <div className="min-w-0 overflow-x-auto">
              <Outlet />
            </div>
          </DivisionNavContext.Provider>

        </div>
      ) : (
        <div className="flex gap-4">
          <aside className="w-64 shrink-0 space-y-4">
            <Card><CardContent className="p-2">{divisionList}</CardContent></Card>
            {division && <Card><CardContent className="p-2">{screenList}</CardContent></Card>}
          </aside>
          <div className="min-w-0 flex-1 space-y-4">
            {current && (
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold">{current.division}</h2>
                <Badge variant="secondary">{current.teams} teams</Badge>
                <Badge variant="outline">{current.pools} pools</Badge>
                <Badge variant="outline">{current.pool_games} pool games</Badge>
                <Badge variant="outline">{current.bracket_games} bracket games</Badge>
                {current.restrictions_pending > 0 && (
                  <Badge className="bg-amber-500 text-white">{current.restrictions_pending} requests pending</Badge>
                )}
              </div>
            )}
            <DivisionNavContext.Provider value={ctx}>
              <Outlet />
            </DivisionNavContext.Provider>
          </div>
        </div>
      )}
    </div>
  );
}
